/**
 * Public marketing-nav auth gates (PBI #1102).
 *
 * Business rule: onboarded free or paid users on marketing pages must not see
 * guest "Sign in" / "Start for Free". Free plan users are included via
 * `!requiresOnboarding`, not `isSubscribed` (free users have no Stripe sub).
 */

/** Inputs for public-nav auth presentation decisions. */
export interface PublicAuthedNavGateInput {
  /** Whether MSAL / local auth has finished initializing. */
  isAuthLoading: boolean;
  /** Whether the user has a valid session. */
  isAuthenticated: boolean;
  /** Whether the user has accepted terms (required before app use). */
  termsAccepted: boolean;
  /** Whether subscription status is still fetching. */
  isSubscriptionLoading: boolean;
  /**
   * Latest subscription status payload, or null when not loaded / fetch failed.
   * Presence of a payload means onboarding flags are trustworthy.
   */
  subscription: { requiresOnboarding: boolean } | null;
}

/**
 * True while auth or subscription status is still settling.
 * Callers should keep the guest CTA slot (no half-ready avatar).
 */
export function isPublicAuthedNavPending(input: PublicAuthedNavGateInput): boolean {
  if (input.isAuthLoading) {
    return true;
  }

  if (!input.isAuthenticated || !input.termsAccepted) {
    return false;
  }

  return input.isSubscriptionLoading;
}

/**
 * Show the Overview-style avatar menu on marketing pages.
 *
 * - Onboarded (status loaded, `requiresOnboarding` false): show menu
 * - Status fetch failed (loaded, `subscription` null): show menu so account/logout remain reachable
 * - Still choosing a plan: false (use {@link shouldShowPublicOnboardingSoftener} instead)
 */
export function shouldShowPublicAuthedNav(input: PublicAuthedNavGateInput): boolean {
  if (!input.isAuthenticated || !input.termsAccepted) {
    return false;
  }

  if (isPublicAuthedNavPending(input)) {
    return false;
  }

  // Fetch failure: status settled but no payload — still treat as signed-in.
  if (input.subscription == null) {
    return true;
  }

  return !input.subscription.requiresOnboarding;
}

/**
 * Authenticated user who still must choose a plan: hide Sign in, keep a single
 * continue CTA into the app (WelcomeOnboarding).
 */
export function shouldShowPublicOnboardingSoftener(input: PublicAuthedNavGateInput): boolean {
  if (!input.isAuthenticated || !input.termsAccepted) {
    return false;
  }

  if (isPublicAuthedNavPending(input)) {
    return false;
  }

  if (input.subscription == null) {
    return false;
  }

  return input.subscription.requiresOnboarding;
}

/**
 * Retarget marketing "Start for Free" / Get Started buttons to Overview.
 * Applies for onboarded users and for the onboarding softener continue path.
 */
export function shouldRetargetMarketingSignupCta(input: PublicAuthedNavGateInput): boolean {
  return shouldShowPublicAuthedNav(input) || shouldShowPublicOnboardingSoftener(input);
}
